/**
 * PER-SERVICE HOSTNAME COMPOSER for the fleet -- the hostname half of the same contract `const/wrangler-fleet-vars.ts`
 * carries for shared `vars` families: compose from the leaf that owns the decision, never transcribe it.
 *
 * MEASURED BASIS (2026-08-20): six service manifests hand-typed fourteen `https://<service>[-dev].federa.com` strings
 * into their `vars` blocks -- a THIRD spelling of the apex domain beside `ACTIVE_BRAND.domains` and the (now deleted)
 * FEDERA_PROD_HOSTNAME. Every one of them encodes exactly two facts the fleet already holds: the brand's apex domain
 * and this service's subdomain label. A rebrand had to find all fourteen by grep, and a miss is silent -- the worker
 * deploys, serves, and hands browsers an absolute url pointing at the previous company.
 *
 * THE GRAMMAR IS THE FLEET'S, READ OFF THE LIVE CONFIGS, not an invention: production is `<label>.<apex>` and
 * development is `<label>-dev.<apex>`, with the host app taking the apex itself (`federa.com`) and `dev.<apex>` for its
 * dev slot. `brandServiceUrls(null)` is that host-app case; the sibling test pins both shapes against the
 * `ACTIVE_BRAND.domains` members that spell them independently (`admin` / `devAdmin` / `primary` / `devPrimary`), so the
 * composer and the identity record cannot drift apart in silence.
 *
 * THE GRAMMAR TAKES ITS APEX AS A PARAMETER (2026-09-18) because a product can answer on an apex of its own:
 * `kenecting.com` is where the Connect desk's host answers, under the same grammar, and its identity leaf re-spelled
 * the scheme, the dev label and the host-app arm beside this file until then. The hostname half is exported for the
 * same reason -- a custom-domain route (`WranglerEnvManifest.customDomain`) binds a bare HOST, not an origin.
 *
 * NOT the worker DEPLOY name. `WORKER_DEPLOY_NAMES` (const/worker-deploy-names.ts) governs the Cloudflare SCRIPT name,
 * a different string class that merely resembles these labels (`analytics-dev` the script, `analytics-dev.federa.com`
 * the host) and diverges on six services. Never derive one from the other.
 */

import { WRANGLER_ENV_KEYS } from './config-environments.ts';
import { ACTIVE_BRAND } from './brand-registry.ts';
import { dottedUnder } from './dotted-member-name.ts';
import type { WranglerEnvKey } from './config-environments.ts';

/** Scheme every fleet origin is served over; no service is reachable over plain HTTP. */
const HTTPS_SCHEME = 'https://';

/** The label that marks the non-production host -- a suffix on a service subdomain (`analytics-dev.`), the whole label on the apex (`dev.`). */
const DEV_HOST_LABEL = 'dev';

/**
 * The public hostname a service answers on under `apex`, per wrangler env block -- the fleet's grammar, once: the apex
 * itself in production, `dev.` under it for dev, and a subdomain label prefixed where the service has one. A product
 * leaf binds the answer as the hostname its manifest attaches as the env's custom domain
 * (`WranglerEnvManifest.customDomain`), so the deploy creates the record and the certificate itself.
 *
 * @runtime-derived-ssot-helper the one spelling of the host grammar; every fleet host and every product apex reads it.
 * @param apex the apex domain the host sits under (`ACTIVE_BRAND.domains.primary`, or a product's own apex).
 * @param subdomain the service's subdomain LABEL (`'analytics'`, `'admin'`), or `null` for the host app that owns the apex.
 */
export function apexHostnames(apex: string, subdomain: string | null): Readonly<Record<WranglerEnvKey, string>> {
	return {
		[WRANGLER_ENV_KEYS.DEV]: subdomain === null ? `${DEV_HOST_LABEL}.${apex}` : `${subdomain}-${DEV_HOST_LABEL}.${apex}`,
		[WRANGLER_ENV_KEYS.PRODUCTION]: dottedUnder(subdomain, apex),
	};
}

/**
 * The public ORIGIN one hostname answers on -- the scheme over the host.
 *
 * @runtime-derived-ssot-helper composes the same-file `HTTPS_SCHEME` over one host; the origin half of this leaf's
 * hostname SSOT for a caller holding hosts one at a time (`const/registered-app-hosts.ts`), not a second decision.
 */
export function httpsOrigin(hostname: string): string {
	return `${HTTPS_SCHEME}${hostname}`;
}

/**
 * The public ORIGIN each hostname answers on -- the scheme over the host, per env block.
 *
 * @runtime-derived-ssot-helper composes {@link httpsOrigin} over hostnames `apexHostnames` spelled; the origin half of
 * this leaf's hostname SSOT, not a second decision.
 */
export function httpsOrigins(hostnames: Readonly<Record<WranglerEnvKey, string>>): Readonly<Record<WranglerEnvKey, string>> {
	return {
		[WRANGLER_ENV_KEYS.DEV]: httpsOrigin(hostnames[WRANGLER_ENV_KEYS.DEV]),
		[WRANGLER_ENV_KEYS.PRODUCTION]: httpsOrigin(hostnames[WRANGLER_ENV_KEYS.PRODUCTION]),
	};
}

/**
 * The public ORIGIN a fleet service answers on, per wrangler env block -- what a manifest binds as `NEXT_PUBLIC_APP_URL`.
 * Read the env member the manifest is already inside (`brandServiceUrls('analytics').dev`), the same way it reads
 * `FIREBASE_ACCESS_VARS.dev`.
 *
 * @runtime-derived-ssot-helper the apex is read from `ACTIVE_BRAND.domains.primary` at call time, so a brand swap
 * re-skins every fleet host with no edit here.
 */
export function brandServiceUrls(subdomain: string | null): Readonly<Record<WranglerEnvKey, string>> {
	return httpsOrigins(apexHostnames(ACTIVE_BRAND.domains.primary, subdomain));
}
